import { FormEvent, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  Paper,
  ScrollArea,
  Skeleton,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { createFileRoute } from '@tanstack/react-router';
import { LegacyPortal } from '~api';
import { api } from '~portal/api';
import {
  buildOnboardSuccessMessage,
  entitlementLabels,
  isSearchableQuery,
  MAX_QUERY_LENGTH,
  MIN_QUERY_LENGTH,
  statusDisplayOf,
} from './-onboarding';
import { OnboardButton } from './-user-admin';

export const Route = createFileRoute('/_protected/admin/users_/onboarding')({
  component: AdminOnboardingPage,
});

function AdminOnboardingPage() {
  const [queryInput, setQueryInput] = useState('');
  const [submittedQuery, setSubmittedQuery] = useState('');
  const [touched, setTouched] = useState(false);
  const [pendingHit, setPendingHit] = useState<LegacyPortal.LegacyOnboardingHit | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [onboardError, setOnboardError] = useState<string | null>(null);

  const statusQuery = api.legacyOnboarding.status.useQuery();
  const enabled = statusQuery.data?.enabled ?? false;
  const searchable = isSearchableQuery(submittedQuery);

  const searchQuery = api.legacyOnboarding.search.useQuery(
    { q: submittedQuery.trim() },
    { enabled: enabled && searchable },
  );

  const onboardMutation = api.legacyOnboarding.onboard.useMutation({
    onSuccess: (result) => {
      setSuccessMessage(buildOnboardSuccessMessage(result));
      setPendingHit(null);
      setOnboardError(null);
      searchQuery.refetch();
    },
    onError: (err) => setOnboardError(err.message),
  });

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setTouched(true);
    if (!isSearchableQuery(queryInput)) return;
    setSuccessMessage(null);
    setSubmittedQuery(queryInput);
  };

  const closeConfirm = () => {
    if (onboardMutation.isPending) return;
    setPendingHit(null);
    setOnboardError(null);
  };

  const hits = searchQuery.data?.hits ?? [];

  return (
    <Stack gap="lg" p="lg">
      <div>
        <Title order={1}>Onboarding</Title>
        <Text c="dimmed" size="sm" mt={4}>
          Search the legacy h2gc.com subscriber directory and onboard existing subscribers to the
          beta platform. Onboarding creates their organisation and account, approves access and
          sends an invitation email so they can set their own password.
        </Text>
      </div>

      {successMessage && (
        <Alert
          color="green"
          title="Onboarded"
          withCloseButton
          onClose={() => setSuccessMessage(null)}
        >
          {successMessage}
        </Alert>
      )}

      {statusQuery.isLoading ? (
        <Skeleton height={80} />
      ) : !enabled ? (
        <Alert color="orange" title="Legacy directory not configured">
          The backend needs{' '}
          <Text span fw={600}>
            LEGACY_PORTAL_QUERY_BASE_URL
          </Text>{' '}
          and{' '}
          <Text span fw={600}>
            LEGACY_PORTAL_QUERY_HMAC_SECRET
          </Text>{' '}
          set before this search can reach the legacy site.
        </Alert>
      ) : null}

      <Paper withBorder p="md">
        <form onSubmit={handleSubmit}>
          <Group gap="md" wrap="wrap" align="flex-start">
            <Stack gap={4}>
              <TextInput
                aria-label="Search legacy subscribers"
                placeholder="Search by email or name"
                leftSection={<IconSearch size={16} />}
                value={queryInput}
                minLength={2}
                maxLength={MAX_QUERY_LENGTH}
                disabled={!enabled}
                onChange={(event) => setQueryInput(event.currentTarget.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') {
                    setQueryInput('');
                    setSubmittedQuery('');
                    setTouched(false);
                  }
                }}
                w={320}
              />
              {touched && !isSearchableQuery(queryInput) && (
                <Text size="xs" c="red">
                  Enter at least {MIN_QUERY_LENGTH} characters.
                </Text>
              )}
            </Stack>
            <Button type="submit" loading={searchQuery.isFetching} disabled={!enabled}>
              Search
            </Button>
          </Group>
        </form>

        <Stack gap="md" mt="md">
          {searchQuery.isError && (
            <Alert color="red" title="Search failed">
              <Stack gap="sm">
                <Text size="sm">{searchQuery.error.message}</Text>
                <Group>
                  <Button size="xs" variant="light" onClick={() => searchQuery.refetch()}>
                    Retry
                  </Button>
                </Group>
              </Stack>
            </Alert>
          )}

          {onboardError && (
            <Alert
              color="red"
              title="Onboarding failed"
              withCloseButton
              onClose={() => setOnboardError(null)}
            >
              {onboardError}
            </Alert>
          )}

          {!searchable ? (
            <Text c="dimmed" size="sm">
              Enter at least two characters of an email or name to search.
            </Text>
          ) : searchQuery.data && hits.length === 0 ? (
            <Text c="dimmed" size="sm">
              No active legacy subscribers match &ldquo;{submittedQuery.trim()}&rdquo;.
            </Text>
          ) : (
            <>
              {searchQuery.data?.truncated && (
                <Alert color="blue" title="Showing the first 50 matches">
                  Refine the search to narrow the results.
                </Alert>
              )}

              {hits.length > 0 && (
                <ScrollArea>
                  <Table striped highlightOnHover miw={900} stickyHeader>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Name</Table.Th>
                        <Table.Th>Email</Table.Th>
                        <Table.Th>Position</Table.Th>
                        <Table.Th>Organisation</Table.Th>
                        <Table.Th>Entitlements</Table.Th>
                        <Table.Th>Status</Table.Th>
                        <Table.Th>Action</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {hits.map((hit) => (
                        <HitRow
                          key={hit.subscriberId}
                          hit={hit}
                          busy={
                            onboardMutation.isPending &&
                            pendingHit?.subscriberId === hit.subscriberId
                          }
                          onOnboard={() => {
                            setOnboardError(null);
                            setPendingHit(hit);
                          }}
                        />
                      ))}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>
              )}
            </>
          )}
        </Stack>
      </Paper>

      <Modal
        opened={Boolean(pendingHit)}
        onClose={closeConfirm}
        title={pendingHit ? `Onboard ${pendingHit.firstName} ${pendingHit.lastName}?` : 'Onboard'}
        centered
      >
        {pendingHit && (
          <Stack gap="md">
            <Text size="sm">{pendingHit.email}</Text>
            <Text size="sm">{pendingHit.organisation?.name ?? 'No organisation'}</Text>
            <Text size="sm" c="dimmed">
              They will receive a WorkOS invitation email to set their password and will have agent
              access as soon as they accept.
            </Text>
            <Group justify="flex-end" gap="sm">
              <Button variant="default" onClick={closeConfirm} disabled={onboardMutation.isPending}>
                Cancel
              </Button>
              <Button
                color="teal"
                loading={onboardMutation.isPending}
                onClick={() => onboardMutation.mutate({ subscriberId: pendingHit.subscriberId })}
              >
                Onboard
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}

/** @desc One search-result row: identity, organisation, entitlements, status and the Onboard action. */
function HitRow({
  hit,
  busy,
  onOnboard,
}: {
  hit: LegacyPortal.LegacyOnboardingHit;
  busy: boolean;
  onOnboard: () => void;
}) {
  const status = statusDisplayOf(hit);

  return (
    <Table.Tr data-testid={`hit-${hit.subscriberId}`}>
      <Table.Td>
        <Group gap="xs" wrap="nowrap">
          <Text size="sm">
            {hit.firstName} {hit.lastName}
          </Text>
          {hit.organisationAdmin && (
            <Badge size="xs" variant="light">
              Org admin
            </Badge>
          )}
        </Group>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{hit.email}</Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{hit.position ?? '—'}</Text>
      </Table.Td>
      <Table.Td>
        <Group gap="xs" wrap="nowrap">
          <Text size="sm">{hit.organisation?.name ?? '—'}</Text>
          {hit.organisation && !hit.organisation.active && (
            <Badge size="xs" variant="light" color="gray">
              Inactive org
            </Badge>
          )}
        </Group>
      </Table.Td>
      <Table.Td>
        {hit.organisation && hit.organisation.entitlements.length > 0 ? (
          <Group gap={4} wrap="wrap">
            {hit.organisation.entitlements.map((key) => (
              <Badge key={key} size="xs" variant="light" color="blue">
                {entitlementLabels[key]}
              </Badge>
            ))}
          </Group>
        ) : (
          <Text size="sm">—</Text>
        )}
      </Table.Td>
      <Table.Td>
        {status.kind === 'onboarded' ? (
          <Badge color="green" variant="filled" size="sm">
            Onboarded
          </Badge>
        ) : status.kind === 'ineligible' ? (
          <Badge color="gray" variant="light" size="sm">
            {status.label}
          </Badge>
        ) : (
          <Text c="dimmed" size="sm">
            Not onboarded
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        {status.kind === 'onboarded' ? (
          <OnboardButton state="done" />
        ) : status.kind === 'ineligible' ? (
          <Tooltip label={status.label}>
            <Button
              size="xs"
              variant="light"
              aria-label={`Onboard ${hit.email}`}
              data-disabled
              onClick={(event) => event.preventDefault()}
            >
              Onboard
            </Button>
          </Tooltip>
        ) : (
          <OnboardButton
            state="actionable"
            ariaLabel={`Onboard ${hit.email}`}
            onClick={onOnboard}
            loading={busy}
          />
        )}
      </Table.Td>
    </Table.Tr>
  );
}
